import metadata from './block.json';

const DEVICES = ['mobile', 'tablet', 'desktop'];
const COLORS = { cream: '#ece8da', sage: '#cfdbbf', clay: '#e9c7ae' };
const DEFAULTS = metadata.attributes.presets.default;
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const clone = (value) => JSON.parse(JSON.stringify(value));
const instances = new WeakMap();
const title = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const device = (value) => DEVICES.includes(value) ? value : 'desktop';
const escape = (value) => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));

function normalisePresets(input) {
    const raw = object(input) ? input : DEFAULTS;
    const result = {};
    DEVICES.forEach(name => {
        const source = own(raw, name) && object(raw[name]) ? raw[name] : DEFAULTS[name];
        const clean = name === 'mobile' ? clone(DEFAULTS.mobile) : {};
        ['padding', 'radius', 'alignment', 'vertical', 'background'].forEach(key => {
            if (!own(source, key)) return;
            const value = source[key];
            if ((key === 'padding' || key === 'radius') && typeof value === 'number' && Number.isFinite(value)) {
                clean[key] = key === 'padding'
                    ? Math.max(12, Math.min(60, Math.round(value / 4) * 4))
                    : Math.max(0, Math.min(40, Math.round(value)));
            } else if ((key === 'alignment' || key === 'vertical') && ['start', 'center', 'end'].includes(value)) {
                clean[key] = value;
            } else if (key === 'background' && typeof value === 'string' && own(COLORS, value)) {
                clean[key] = value;
            }
        });
        result[name] = clean;
    });
    return result;
}

function effective(values, selected) {
    const name = device(selected);
    return Object.assign({}, values.mobile, name !== 'mobile' ? values.tablet : {}, name === 'desktop' ? values.desktop : {});
}

function stateTokens(presets, selected) {
    const name = device(selected);
    const current = effective(presets, name);
    const tokens = {
        initialDevice: name,
        deviceLabel: title(name),
        padding: current.padding,
        radius: current.radius,
        backgroundHex: COLORS[current.background],
        alignItems: { start: 'flex-start', center: 'center', end: 'flex-end' }[current.alignment],
        textAlign: { start: 'left', center: 'center', end: 'right' }[current.alignment],
        justifyContent: { start: 'flex-start', center: 'center', end: 'flex-end' }[current.vertical],
        inheritanceLabel: name === 'mobile' ? ' · starting values' : own(presets[name], 'padding') ? ' · custom padding' : ' · padding inherited'
    };
    DEVICES.forEach(key => { tokens['device_' + key] = String(key === name); });
    Object.keys(COLORS).forEach(key => { tokens['background_' + key] = String(key === current.background); });
    ['start', 'center', 'end'].forEach(key => { tokens['alignment_' + key] = key === current.alignment ? 'selected' : ''; });
    return tokens;
}

/** Both the editor and PHP renderer use template.html. All inserted copy is escaped. */
function markup(template, attributes, uid) {
    const presets = normalisePresets(attributes.presets);
    const selected = device(attributes.initialDevice);
    const tokens = Object.assign({}, attributes, stateTokens(presets, selected), {
        uid: String(uid).replace(/[^a-zA-Z0-9_-]/g, ''),
        config: JSON.stringify({ initialDevice: selected, presets: presets }),
        showHeading: attributes.showHeading !== false ? 'true' : 'false',
        showCaption: attributes.showCaption !== false ? 'true' : 'false'
    });
    return template.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (match, key) => own(tokens, key) ? escape(tokens[key]) : '');
}

function mount(root) {
    if (!root || !root.querySelector) return null;
    if (instances.has(root)) return instances.get(root);
    let config;
    try { config = JSON.parse(root.getAttribute('data-config') || '{}'); } catch (error) { config = {}; }
    if (!object(config)) config = {};
    const starting = normalisePresets(config.presets);
    let values = clone(starting);
    let selected = device(config.initialDevice);
    const find = selector => root.querySelector(selector);
    const all = selector => Array.from(root.querySelectorAll(selector));
    const preview = find('.jgb-lp-preview');
    const padding = find('input[type="range"]');
    const alignment = find('select');
    const output = find('output');
    const active = find('.jgb-lp-inspector-heading strong');
    const inheritance = find('[id$="-inheritance-label"]');
    const status = find('[aria-live]');
    const buttons = all('.jgb-lp-device');
    const swatches = all('.jgb-lp-swatch');
    const reset = find('.jgb-lp-reset');
    if (!preview || !padding || !alignment || !output || !active || !inheritance || !status || !reset) return null;

    function render(announce) {
        const current = effective(values, selected);
        const tokens = stateTokens(values, selected);
        preview.dataset.device = selected;
        preview.style.setProperty('--jgb-demo-bg', tokens.backgroundHex);
        preview.style.setProperty('--jgb-demo-radius', tokens.radius + 'px');
        preview.style.setProperty('--jgb-demo-padding', tokens.padding + 'px');
        preview.style.setProperty('--jgb-demo-items', tokens.alignItems);
        preview.style.setProperty('--jgb-demo-text', tokens.textAlign);
        preview.style.setProperty('--jgb-demo-justify', tokens.justifyContent);
        padding.value = String(current.padding);
        output.textContent = current.padding + ' px';
        alignment.value = current.alignment;
        active.textContent = title(selected);
        inheritance.textContent = tokens.inheritanceLabel;
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.device === selected)));
        swatches.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.color === current.background)));
        if (announce) {
            const alignLabel = { start: 'left', center: 'center', end: 'right' }[current.alignment];
            status.textContent = title(selected) + ' example: ' + current.padding + ' pixel padding, ' + alignLabel + ' aligned content, ' + current.background + ' background.';
        }
    }
    function onClick(event) {
        const target = event.target.closest && event.target.closest('button');
        if (!target || !root.contains(target) || target.disabled) return;
        if (target.classList.contains('jgb-lp-device') && DEVICES.includes(target.dataset.device)) {
            selected = target.dataset.device;
        } else if (target.classList.contains('jgb-lp-swatch') && own(COLORS, target.dataset.color)) {
            values[selected].background = target.dataset.color;
        } else if (target === reset) {
            values = clone(starting);
            render(false);
            status.textContent = 'Demo reset. ' + title(selected) + ' example selected.';
            return;
        } else { return; }
        // Do not swallow the click: the Gutenberg block should remain selectable.
        render(true);
    }
    function onInput(event) {
        if (event.target !== padding) return;
        const value = Number(padding.value);
        if (Number.isFinite(value)) values[selected].padding = Math.max(12, Math.min(60, Math.round(value / 4) * 4));
        render(false);
    }
    function onChange(event) {
        if (event.target === padding) {
            onInput(event);
            render(true);
        } else if (event.target === alignment && ['start', 'center', 'end'].includes(alignment.value)) {
            values[selected].alignment = alignment.value;
            render(true);
        }
    }
    root.addEventListener('click', onClick);
    root.addEventListener('input', onInput);
    root.addEventListener('change', onChange);
    [padding, alignment, reset, ...buttons, ...swatches].forEach(control => { control.disabled = false; });
    render(false);
    const controller = {
        destroy: function () {
            root.removeEventListener('click', onClick);
            root.removeEventListener('input', onInput);
            root.removeEventListener('change', onChange);
            instances.delete(root);
        }
    };
    instances.set(root, controller);
    return controller;
}

export { mount, markup, normalisePresets, effective, stateTokens, DEFAULTS, COLORS, DEVICES };
